import { queryOptions, type QueryClient } from '@tanstack/react-query';
import type { ClientStatus, Priority, SprintState, WorkType } from '@rundown/types';
import { api } from '../../lib/api';

/**
 * Sprints (delivery) transport — the client-facing "what has Noorix committed
 * to, by sprint" read model. Distinct from `../planning`'s CRUD `sprints`
 * endpoint, which creates and locks sprints; this one only reads the scope
 * that endpoint's lock action freezes.
 *
 * ── ASSUMED CONTRACT ────────────────────────────────────────────────────────
 *   GET /workspaces/:workspaceId/delivery/sprints -> DeliverySprint[]
 *
 * Query keys are local rather than added to lib/queryKeys.ts, which other
 * agents own concurrently while this milestone is built — see that file's own
 * HARD RULE comment for why every key here still starts
 * `['ws', workspaceId, …]` regardless of which file declares it.
 */

/** The client/internal split, same shape as RequestRow in features/requests/api.ts
 * — a client-role response never CONTAINS the fields a client may not see. */
export interface DeliverySprintItem {
  readonly requestId: string;
  readonly number: number;
  readonly title: string;
  readonly type: WorkType;
  readonly priority: Priority;
  /** The project, by name — frozen at lock on a locked sprint's rows. */
  readonly projectName: string | null;
  readonly approxHours: number;
  readonly ownerName: string | null;
  /** Live — the frozen row says what was agreed, this says how it is going. */
  readonly status: ClientStatus;
  readonly closedAt: string | null;
  /** Taken out by an agreed change of plan. The item stays listed, marked, as evidence. */
  readonly removedAt: string | null;
}

export interface InternalDeliverySprintItem extends DeliverySprintItem {
  readonly ownerId: string | null;
  readonly visibleToClient: boolean;
  /** Present only once a sprint has locked — see DeliverySprint's `frozen`. */
  readonly addedAfterLock?: boolean;
}

/** `ownerId` exists only on the internal projection, the same tell
 * `isInternalRequest`/`isInternalBacklogRow` use elsewhere. */
export function isInternalSprintItem(item: DeliverySprintItem): item is InternalDeliverySprintItem {
  return 'ownerId' in item;
}

export interface DeliverySprint {
  readonly id: string;
  readonly key: string;
  readonly name: string;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly state: SprintState;
  readonly lockedAt: string | null;
  readonly startedAt: string | null;
  readonly releasedAt: string | null;
  /**
   * True when `items` is the frozen `sprint_items` snapshot taken at lock —
   * the evidentiary scope a billing dispute can be settled against. False
   * means the sprint is still `planning`: nothing has locked, and `items` is
   * the LIVE set of requests currently pointed at it, which can still move
   * before it does.
   */
  readonly frozen: boolean;
  readonly items: readonly DeliverySprintItem[];
  readonly plannableHours: number;
  readonly reserveHours: number;
  readonly hotfixes: readonly DeliveryHotfix[];
  readonly hotfixHours: number;
}

export interface DeliveryHotfix {
  readonly requestId: string;
  readonly number: number;
  readonly title: string;
  readonly type: WorkType;
  readonly priority: Priority;
  readonly status: ClientStatus;
  readonly closedAt: string | null;
}

function sprintsPath(workspaceId: string): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}/delivery/sprints`;
}

export function fetchDeliverySprints(workspaceId: string, signal?: AbortSignal): Promise<DeliverySprint[]> {
  return api.get<DeliverySprint[]>(sprintsPath(workspaceId), signal);
}

/** Every key here starts `['ws', workspaceId]` — see lib/queryKeys.ts's HARD
 * RULE, which this local set still follows even though it is not declared in
 * that shared file. */
export const deliverySprintsKeys = {
  list: (workspaceId: string) => ['ws', workspaceId, 'delivery', 'sprints'] as const,
};

export function deliverySprintsQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: deliverySprintsKeys.list(workspaceId),
    queryFn: ({ signal }) => fetchDeliverySprints(workspaceId, signal),
  });
}

/**
 * The bare sprint list, for choosing one.
 *
 * Deliberately the CRUD route rather than `/delivery/sprints` above: that one
 * carries every sprint's full item list, which is a great deal of payload to
 * fetch in order to populate a `<select>` with six names. This route is
 * ungated for reads — see SprintsController's own comment on why a sprint's
 * name and dates are not sensitive the way `internal_title` is.
 */
export interface SprintSummary {
  readonly id: string;
  readonly key: string;
  readonly name: string;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly state: SprintState;
  readonly sortIndex: number;
  readonly lockedAt: string | null;
  readonly startedAt: string | null;
  readonly releasedAt: string | null;
}

export function fetchSprints(workspaceId: string, signal?: AbortSignal): Promise<SprintSummary[]> {
  return api.get<SprintSummary[]>(`/workspaces/${encodeURIComponent(workspaceId)}/sprints`, signal);
}

export const sprintsKeys = {
  list: (workspaceId: string) => ['ws', workspaceId, 'sprints'] as const,
};

export function sprintsQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: sprintsKeys.list(workspaceId),
    queryFn: ({ signal }) => fetchSprints(workspaceId, signal),
    // Sprints are created a handful of times a quarter. Refetching this to fill
    // a picker on every request screen is work nobody asked for.
    staleTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// Sprint lifecycle (Noorix) — see sprints.service.ts
// ---------------------------------------------------------------------------

function sprintPath(workspaceId: string, sprintId?: string): string {
  const base = `/workspaces/${encodeURIComponent(workspaceId)}/sprints`;
  return sprintId === undefined ? base : `${base}/${encodeURIComponent(sprintId)}`;
}

export function appendSprint(workspaceId: string): Promise<SprintSummary> {
  return api.post<SprintSummary>(`${sprintPath(workspaceId)}/append`);
}

/**
 * Commits `requestIds` (what was on the column) and freezes the scope, in one
 * transaction. Does NOT begin the week — that is `startSprint` below.
 */
export function lockSprint(
  workspaceId: string,
  sprintId: string,
  requestIds: readonly string[],
): Promise<SprintSummary> {
  return api.post<SprintSummary>(`${sprintPath(workspaceId, sprintId)}/lock`, { requestIds });
}

/** Begin the week on a locked sprint. Refused (409) if another is running. */
export function startSprint(workspaceId: string, sprintId: string): Promise<SprintSummary> {
  return api.post<SprintSummary>(`${sprintPath(workspaceId, sprintId)}/start`);
}

/** Take a start back, keeping the lock. Refused once hours are logged. */
export function unstartSprint(workspaceId: string, sprintId: string): Promise<SprintSummary> {
  return api.post<SprintSummary>(`${sprintPath(workspaceId, sprintId)}/unstart`);
}

/**
 * Refuses (409) while anything committed to the sprint is unfinished — see
 * SprintsService.update. Carry those items over first, or finish them on the
 * Board.
 */
export function releaseSprint(workspaceId: string, sprintId: string): Promise<SprintSummary> {
  return api.patch<SprintSummary>(sprintPath(workspaceId, sprintId), { state: 'released' });
}

/** Take named unfinished work out of a locked sprint, back to the top of the backlog. */
export function carryOverItems(
  workspaceId: string,
  sprintId: string,
  requestIds: readonly string[],
): Promise<SprintSummary> {
  return api.post<SprintSummary>(`${sprintPath(workspaceId, sprintId)}/carry-over`, { requestIds });
}

/** Admin corrections: undo a Lock, delete a sprint never locked, fix name/dates. */
export function unlockSprint(workspaceId: string, sprintId: string): Promise<SprintSummary> {
  return api.post<SprintSummary>(`${sprintPath(workspaceId, sprintId)}/unlock`);
}

export function deleteSprint(workspaceId: string, sprintId: string): Promise<void> {
  return api.delete<void>(sprintPath(workspaceId, sprintId));
}

export interface EditSprintInput {
  readonly name?: string;
  readonly startsOn?: string;
  readonly endsOn?: string;
  /**
   * Move every later sprint by the same amount this one's END date moved —
   * see UpdateSprintSchema for why the end and not the start. Refused if a
   * later sprint has already started.
   */
  readonly cascade?: boolean;
}

export function editSprint(
  workspaceId: string,
  sprintId: string,
  input: EditSprintInput,
): Promise<SprintSummary> {
  return api.patch<SprintSummary>(sprintPath(workspaceId, sprintId), input);
}

// ---------------------------------------------------------------------------
// Changes of plan — see plan-changes.service.ts
// ---------------------------------------------------------------------------

export type PlanChangeStatus = 'open' | 'agreed' | 'declined';

export interface PlanChangeItem {
  /** Null when the client may not see which request it was ("Internal work"). */
  readonly requestId: string | null;
  readonly number: number | null;
  readonly title: string;
  readonly type: WorkType | null;
  readonly approxHours: number;
  readonly status: ClientStatus;
}

export interface PlanChange {
  readonly id: string;
  readonly sprintId: string;
  readonly sprintName: string;
  readonly status: PlanChangeStatus;
  readonly reason: string;
  readonly add: PlanChangeItem | null;
  readonly remove: PlanChangeItem | null;
  readonly raisedByName: string | null;
  readonly raisedAt: string;
  readonly decidedByName: string | null;
  readonly decidedAt: string | null;
  readonly decisionNote: string | null;
  readonly hoursDelta: number | null;
}

export function fetchPlanChanges(workspaceId: string, signal?: AbortSignal): Promise<PlanChange[]> {
  return api.get<PlanChange[]>(`/workspaces/${encodeURIComponent(workspaceId)}/plan-changes`, signal);
}

export interface RaisePlanChangeInput {
  readonly addRequestId: string | null;
  readonly removeRequestId: string | null;
  readonly reason: string;
}

export function raisePlanChange(
  workspaceId: string,
  sprintId: string,
  input: RaisePlanChangeInput,
): Promise<PlanChange> {
  return api.post<PlanChange>(`${sprintPath(workspaceId, sprintId)}/plan-changes`, input);
}

export function decidePlanChange(
  workspaceId: string,
  planChangeId: string,
  decision: 'agree' | 'decline',
  note?: string,
): Promise<PlanChange> {
  return api.post<PlanChange>(
    `/workspaces/${encodeURIComponent(workspaceId)}/plan-changes/${encodeURIComponent(planChangeId)}/decide`,
    { decision, note },
  );
}

export const planChangeKeys = {
  list: (workspaceId: string) => ['ws', workspaceId, 'plan-changes'] as const,
};

export function planChangesQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: planChangeKeys.list(workspaceId),
    queryFn: ({ signal }) => fetchPlanChanges(workspaceId, signal),
  });
}

/**
 * Everything a sprint write can move. Starting a sprint changes the backlog,
 * the projection, the board's lanes, the request rows and the sprint list at
 * once, so every planning mutation refreshes the lot rather than each call
 * site guessing which of them it touched.
 */
export function invalidatePlanning(queryClient: QueryClient, workspaceId: string): Promise<void> {
  return Promise.all(
    [
      ['ws', workspaceId, 'delivery'],
      ['ws', workspaceId, 'sprints'],
      ['ws', workspaceId, 'backlog'],
      ['ws', workspaceId, 'board'],
      ['ws', workspaceId, 'plan-changes'],
      ['ws', workspaceId, 'requests'],
    ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  ).then(() => undefined);
}
